import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import SEO from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import SignInModal from '../../components/SignInModal';
import ImageCropper from '../../components/ImageCropper';
import { cardAPI } from '../../services/api';
import {
  Mail,
  User,
  Building,
  Phone,
  Globe,
  Lock,
  Palette,
  Copy,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Briefcase,
  Camera,
  Image as ImageIcon,
  Sparkles,
  Eye,
  Crop
} from 'lucide-react';

const STEPS = [
  { id: 1, title: 'Basic Info', icon: User },
  { id: 2, title: 'Photo', icon: Camera },
  { id: 3, title: 'Contact', icon: Mail },
  { id: 4, title: 'Template', icon: Palette },
];

// Same templates as bio page email signature
const TEMPLATES = [
  { id: 'classic', name: 'Classic', description: 'Clean professional layout' },
  { id: 'modern', name: 'Modern', description: 'With colored QR border' },
  { id: 'minimal', name: 'Minimal', description: 'Ultra clean text only' },
  { id: 'compact', name: 'Executive', description: 'Premium compact style' },
  { id: 'banner', name: 'Banner', description: 'Full width colored header' }
];

const EmailSignatureGenerator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    company: '',
    email: '',
    phone: '',
    website: '',
  });
  
  const [profileImage, setProfileImage] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('classic');
  const [showPreview, setShowPreview] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [isCreatingCard, setIsCreatingCard] = useState(false);
  const [pendingSignIn, setPendingSignIn] = useState(false);
  
  // Image cropping state
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);

  // Swipe gesture state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);
  const minSwipeDistance = 50;

  const brandColor = '#f59e0b'; // Default amber color

  // Swipe gesture handlers
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchEndX(null);
    setTouchStart(e.targetTouches[0].clientY);
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || !touchStartX || !touchEndX) return;
    
    const distanceY = touchStart - touchEnd;
    const distanceX = touchStartX - touchEndX;
    
    const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY);
    
    if (isHorizontalSwipe) {
      const isSwipeLeft = distanceX > minSwipeDistance;
      const isSwipeRight = distanceX < -minSwipeDistance;
      
      if (isSwipeLeft && !showPreview && currentStep < 4) {
        nextStep();
      } else if (isSwipeRight && !showPreview && currentStep > 1) {
        prevStep();
      }
    }
    // Removed swipe up/down for preview - only keep horizontal swipe for step navigation
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        // Show cropper with the uploaded image
        setImageToCrop(e.target.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = (croppedImage) => {
    setProfileImage(croppedImage);
    setImageToCrop(null);
  };

  const nextStep = () => {
    if (currentStep === 1 && !formData.name.trim()) {
      toast({ title: 'Name Required', description: 'Please enter your name', variant: 'destructive' });
      return;
    }
    if (currentStep === 3 && !formData.email.trim()) {
      toast({ title: 'Email Required', description: 'Please enter your email address', variant: 'destructive' });
      return;
    }
    if (currentStep < 4) {
      setCurrentStep(prev => prev + 1);
    } else {
      setShowPreview(true);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  // Create card and redirect to bio page with email signature popup
  const handleCreateAndCopy = async () => {
    if (!user) {
      setPendingSignIn(true);
      setShowSignInModal(true);
      return;
    }
    await createCardAndRedirect();
  };

  // Actual card creation logic
  const createCardAndRedirect = async () => {
    if (!user) {
      toast({ title: 'Error', description: 'Please sign in first', variant: 'destructive' });
      return;
    }
    
    setIsCreatingCard(true);
    try {
      const existingCards = await cardAPI.getByUserId(user.uid);
      
      if (existingCards && Array.isArray(existingCards) && existingCards.length > 0) {
        const existingCard = existingCards[0];
        navigate(`/bio/${existingCard.username}?openSignature=true`);
        return;
      }
      
      const userEmail = formData.email || user.email;
      if (!userEmail) {
        toast({ title: 'Email Required', description: 'Please provide an email address', variant: 'destructive' });
        setIsCreatingCard(false);
        return;
      }
      
      const cardData = {
        userId: user.uid,
        name: formData.name || user.displayName || 'My Card',
        title: formData.title || '',
        company: formData.company || '',
        email: userEmail,
        phone: formData.phone || '',
        website: formData.website || '',
        profileImage: profileImage || user.photoURL || '',
        bioTemplate: 'modern'
      };
      
      const response = await cardAPI.create(cardData);
      
      if (response && response.username) {
        navigate(`/bio/${response.username}?openSignature=true`);
      } else {
        toast({ title: 'Card Created', description: 'Redirecting to complete your card...' });
        navigate('/card-builder');
      }
    } catch (error) {
      console.error('Error in createCardAndRedirect:', error);
      const errorMessage = error?.response?.data?.detail || 'Could not create card. Please try again.';
      toast({ title: 'Error', description: errorMessage, variant: 'destructive' });
      navigate('/card-builder');
    } finally {
      setIsCreatingCard(false);
    }
  };

  // Handle post-sign-in
  useEffect(() => {
    if (user && pendingSignIn) {
      setPendingSignIn(false);
      setShowSignInModal(false);
      setTimeout(() => {
        createCardAndRedirect();
      }, 500);
    }
  }, [user, pendingSignIn]);

  // Generate email signature HTML (same as bio page)
  const generateSignatureHTML = () => {
    // Apply minimum lengths for better visual consistency
    const name = formData.name || 'Your Name';
    const title = formData.title || 'Your Title';
    const company = formData.company || 'Company';
    const email = formData.email || '';
    const phone = formData.phone || '';
    const website = formData.website || '';
    
    // Default placeholder image for consistent layout
    const defaultProfileImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOTAiIGhlaWdodD0iOTAiIHZpZXdCb3g9IjAgMCA5MCA5MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iOTAiIGhlaWdodD0iOTAiIHJ4PSIxMiIgZmlsbD0iI0YzRjRGNiIvPjxjaXJjbGUgY3g9IjQ1IiBjeT0iMzIiIHI9IjE0IiBmaWxsPSIjRDFEM0Q4Ii8+PHBhdGggZD0iTTY4IDc1QzY4IDYxLjE5MyA1Ny44MDcgNTAgNDQgNTBDMzAuMTkzIDUwIDIwIDYxLjE5MyAyMCA3NUg2OFoiIGZpbGw9IiNEMUQzRDgiLz48L3N2Zz4=';
    const displayImage = profileImage || defaultProfileImage;
    
    const piperBranding = `
  <tr>
    <td colspan="3" style="padding-top: 12px;">
      <a href="https://pipercard.in" style="font-size: 10px; color: #999; text-decoration: none; font-family: Arial, sans-serif;">Made with <span style="color: #f59e0b; font-weight: 600;">Piper Card</span></a>
    </td>
  </tr>`;

    const qrPlaceholder = `
      <div style="width: 80px; height: 80px; background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); border-radius: 12px; display: flex; align-items: center; justify-content: center;">
        <span style="color: white; font-size: 10px; text-align: center; font-family: Arial;">QR Code<br/>Preview</span>
      </div>`;

    if (selectedTemplate === 'classic') {
      return `
<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, Helvetica, sans-serif;">
  <tr>
    <td style="background: #ffffff; border: 1px solid #e0e0e0; border-radius: 16px; padding: 20px;">
      <table cellpadding="0" cellspacing="0" border="0">
        <tr>
          <td style="vertical-align: top; padding-right: 16px;">
            <img src="${displayImage}" width="90" height="90" style="border-radius: 12px; display: block; object-fit: cover;" alt="${name}">
          </td>
          <td style="vertical-align: top; padding-right: 16px; min-width: 180px;">
            <p style="margin: 0; font-size: 18px; font-weight: 600; color: #1a1a1a;">${name}</p>
            <p style="margin: 3px 0 0 0; font-size: 14px; color: #555;">${title}</p>
            <p style="margin: 2px 0 0 0; font-size: 14px; color: #777;">${company}</p>
            <table cellpadding="0" cellspacing="0" border="0" style="margin-top: 10px; font-size: 13px; color: #444;">
              ${phone ? `<tr><td style="padding: 2px 0;"><a href="tel:${phone}" style="color: #444; text-decoration: none;">${phone}</a></td></tr>` : ''}
              ${email ? `<tr><td style="padding: 2px 0;"><a href="mailto:${email}" style="color: #444; text-decoration: none;">${email}</a></td></tr>` : ''}
            </table>
          </td>
          <td style="vertical-align: top;">${qrPlaceholder}</td>
        </tr>
      </table>
      ${piperBranding}
    </td>
  </tr>
</table>`;
    }

    if (selectedTemplate === 'modern') {
      return `
<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, Helvetica, sans-serif;">
  <tr>
    <td style="background: #ffffff; border: 1px solid #e0e0e0; border-radius: 16px; padding: 20px;">
      <table cellpadding="0" cellspacing="0" border="0">
        <tr>
          <td style="vertical-align: top; padding-right: 16px;">
            <img src="${displayImage}" width="90" height="90" style="border-radius: 12px; display: block; object-fit: cover;" alt="${name}">
          </td>
          <td style="vertical-align: top; padding-right: 16px; min-width: 180px;">
            <p style="margin: 0; font-size: 18px; font-weight: 600; color: #1a1a1a;">${name}</p>
            <p style="margin: 3px 0 0 0; font-size: 13px; color: #555;">${title}</p>
            <p style="margin: 2px 0 0 0; font-size: 13px; color: #777;">${company}</p>
            <table cellpadding="0" cellspacing="0" border="0" style="margin-top: 8px; font-size: 12px; color: #555;">
              ${phone ? `<tr><td style="padding: 2px 0;">${phone}</td></tr>` : ''}
              ${email ? `<tr><td style="padding: 2px 0;">${email}</td></tr>` : ''}
            </table>
          </td>
          <td style="vertical-align: top;">
            <div style="background: ${brandColor}; border-radius: 12px; padding: 8px;">${qrPlaceholder}</div>
          </td>
        </tr>
      </table>
      ${piperBranding}
    </td>
  </tr>
</table>`;
    }

    if (selectedTemplate === 'minimal') {
      return `
<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, Helvetica, sans-serif;">
  <tr>
    <td style="background: #ffffff; border: 1px solid #e0e0e0; border-radius: 16px; padding: 20px;">
      <table cellpadding="0" cellspacing="0" border="0">
        <tr>
          <td style="vertical-align: middle; padding-right: 16px;">
            <img src="${displayImage}" width="70" height="70" style="border-radius: 10px; display: block; object-fit: cover;" alt="${name}">
          </td>
          <td style="vertical-align: middle; padding-right: 16px; min-width: 180px;">
            <p style="margin: 0; font-size: 17px; font-weight: 600; color: #1a1a1a;">${name}</p>
            <p style="margin: 4px 0 0 0; font-size: 13px; color: #666;">${title} · ${company}</p>
            <p style="margin: 8px 0 0 0; font-size: 12px; color: #888;">
              ${phone ? `<span>${phone}</span>` : ''}${phone && email ? ' · ' : ''}${email ? `<span>${email}</span>` : ''}
            </p>
          </td>
          <td style="vertical-align: middle;">${qrPlaceholder}</td>
        </tr>
      </table>
      ${piperBranding}
    </td>
  </tr>
</table>`;
    }

    if (selectedTemplate === 'compact') {
      return `
<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, Helvetica, sans-serif;">
  <tr>
    <td style="background: #ffffff; border: 1px solid #e0e0e0; border-radius: 16px; padding: 20px;">
      <table cellpadding="0" cellspacing="0" border="0">
        <tr>
          <td style="vertical-align: top; padding-right: 16px;">
            <img src="${displayImage}" width="90" height="90" style="border-radius: 12px; display: block; object-fit: cover;" alt="${name}">
          </td>
          <td style="vertical-align: middle; padding-right: 16px; min-width: 180px;">
            <p style="margin: 0; font-size: 16px; font-weight: 600; color: #1a1a1a;">${name}</p>
            <p style="margin: 2px 0 0 0; font-size: 13px; color: #555;">${title}</p>
            <p style="margin: 2px 0 0 0; font-size: 13px; color: #777;">${company}</p>
            <p style="margin: 6px 0 0 0; font-size: 12px; color: #888;">${phone || ''}${phone && email ? ' · ' : ''}${email || ''}</p>
          </td>
          <td style="vertical-align: middle;">${qrPlaceholder}</td>
        </tr>
      </table>
      ${piperBranding}
    </td>
  </tr>
</table>`;
    }

    if (selectedTemplate === 'banner') {
      return `
<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, Helvetica, sans-serif;">
  <tr>
    <td style="border: 1px solid #e0e0e0; border-radius: 16px; overflow: hidden;">
      <table cellpadding="0" cellspacing="0" border="0" width="400">
        <tr>
          <td style="background: ${brandColor}; padding: 16px 20px;">
            <table cellpadding="0" cellspacing="0" border="0" width="100%">
              <tr>
                <td style="width: 60px; vertical-align: middle;">
                  <img src="${displayImage}" width="55" height="55" style="border-radius: 50%; border: 3px solid white; display: block; object-fit: cover;" alt="${name}">
                </td>
                <td style="vertical-align: middle; padding-left: 14px;">
                  <p style="margin: 0; font-size: 18px; font-weight: 700; color: white;">${name}</p>
                  <p style="margin: 3px 0 0 0; font-size: 12px; color: rgba(255,255,255,0.9);">${title}</p>
                  <p style="margin: 2px 0 0 0; font-size: 12px; color: rgba(255,255,255,0.85);">${company}</p>
                </td>
                <td style="width: 75px; text-align: right; vertical-align: middle;">
                  <div style="width: 65px; height: 65px; background: white; border-radius: 6px; display: flex; align-items: center; justify-content: center;">
                    <span style="color: ${brandColor}; font-size: 8px; text-align: center;">QR</span>
                  </div>
                </td>
              </tr>
            </table>
          </td>
        </tr>
        <tr>
          <td style="background: white; padding: 12px 20px;">
            <table cellpadding="0" cellspacing="0" border="0" style="font-size: 12px; color: #555;">
              <tr>
                ${phone ? `<td style="padding-right: 20px;"><a href="tel:${phone}" style="color: #555; text-decoration: none;">${phone}</a></td>` : ''}
                ${email ? `<td style="padding-right: 20px;"><a href="mailto:${email}" style="color: #555; text-decoration: none;">${email}</a></td>` : ''}
                ${website ? `<td><a href="${website.startsWith('http') ? website : 'https://' + website}" style="color: ${brandColor}; text-decoration: none; font-weight: 500;">${website.replace(/^https?:\/\//, '')}</a></td>` : ''}
              </tr>
            </table>
          </td>
        </tr>
        <tr>
          <td style="background: #f9f9f9; padding: 8px 20px; border-top: 1px solid #eee;">
            <a href="https://pipercard.in" style="font-size: 10px; color: #999; text-decoration: none;">Made with <span style="color: #f59e0b; font-weight: 600;">Piper Card</span></a>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;
    }

    return '';
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-sm mb-1.5 block">Full Name <span className="text-red-500">*</span></Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="John Doe"
                  className="pl-10 h-12"
                  autoFocus
                  data-testid="input-name"
                  minLength={3}
                />
              </div>
              {formData.name && formData.name.length < 3 && (
                <p className="text-xs text-amber-600 mt-1">Name should be at least 3 characters for best results</p>
              )}
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">Job Title <span className="text-red-500">*</span></Label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="Marketing Manager"
                  className="pl-10 h-12"
                  data-testid="input-title"
                  minLength={3}
                />
              </div>
              {formData.title && formData.title.length < 3 && (
                <p className="text-xs text-amber-600 mt-1">Job title should be at least 3 characters for best results</p>
              )}
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">Company <span className="text-red-500">*</span></Label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={formData.company}
                  onChange={(e) => handleChange('company', e.target.value)}
                  placeholder="Acme Inc"
                  className="pl-10 h-12"
                  data-testid="input-company"
                  minLength={2}
                />
              </div>
              {formData.company && formData.company.length < 2 && (
                <p className="text-xs text-amber-600 mt-1">Company name should be at least 2 characters for best results</p>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2 bg-amber-50 p-3 rounded-lg border border-amber-100">
              <Sparkles className="w-4 h-4 inline mr-1 text-amber-500" />
              Fill all fields for a professional-looking signature
            </p>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium block">Profile Photo (Optional)</Label>
            <p className="text-xs text-gray-500 -mt-2">Add your photo to personalize your signature</p>
            <div className="flex items-center gap-4">
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="w-24 h-24 rounded-xl bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-amber-400 hover:bg-amber-50 transition-all overflow-hidden"
              >
                {profileImage ? (
                  <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center">
                    <Camera className="w-8 h-8 text-gray-400 mx-auto" />
                    <span className="text-xs text-gray-500 mt-1">Add Photo</span>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-2">Upload a professional photo</p>
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} className="text-sm">
                  <ImageIcon className="w-4 h-4 mr-2" /> Choose File
                </Button>
                {profileImage && (
                  <Button variant="ghost" size="sm" onClick={() => setProfileImage(null)} className="ml-2 text-red-500 hover:text-red-600">
                    Remove
                  </Button>
                )}
              </div>
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-sm mb-1.5 block">Email <span className="text-red-500">*</span></Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="john@acme.com" className="pl-10 h-12" data-testid="input-email" />
              </div>
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">Phone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} placeholder="+91 98765 43210" className="pl-10 h-12" data-testid="input-phone" />
              </div>
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">Website</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input value={formData.website} onChange={(e) => handleChange('website', e.target.value)} placeholder="www.acme.com" className="pl-10 h-12" data-testid="input-website" />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium block mb-3">
              <Palette className="w-4 h-4 inline mr-1" /> Choose Template
            </Label>
            <div className="space-y-2">
              {TEMPLATES.map((template) => (
                <button
                  key={template.id}
                  onClick={() => setSelectedTemplate(template.id)}
                  className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                    selectedTemplate === template.id
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                  data-testid={`template-${template.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-gray-900">{template.name}</span>
                      <p className="text-xs text-gray-500 mt-0.5">{template.description}</p>
                    </div>
                    {selectedTemplate === template.id && <Check className="w-5 h-5 text-amber-500" />}
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Layout>
      <SEO
        title="Free Email Signature Generator | Professional HTML Signatures"
        description="Create professional email signatures for Gmail, Outlook, Apple Mail. Free HTML signature generator with templates. No coding required."
        keywords="email signature generator, free email signature, HTML signature maker, Gmail signature, Outlook signature"
      />

      <div 
        className="min-h-screen bg-gray-50"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="w-6 h-6 text-amber-500" />
              <span className="font-bold text-lg text-gray-900">Email Signature Generator</span>
            </div>
            <button onClick={() => setShowPreview(true)} className="text-amber-600 font-medium text-sm hover:text-amber-700 lg:hidden" data-testid="preview-button">
              Preview
            </button>
          </div>
        </header>

        {/* Main Content - Full Width on Desktop */}
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
            {/* Left Column: Steps Form */}
            <div>
              {/* Progress Steps */}
              <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  {STEPS.map((step, index) => (
                    <div key={step.id} className="flex items-center">
                      <button
                        onClick={() => {
                          if (step.id < currentStep || (step.id > 1 && formData.name)) {
                            setCurrentStep(step.id);
                          }
                        }}
                        className={`flex items-center gap-2 ${currentStep >= step.id ? 'text-amber-600' : 'text-gray-400'}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                          currentStep > step.id ? 'bg-amber-500 text-white' :
                          currentStep === step.id ? 'bg-amber-100 text-amber-600 border-2 border-amber-500' :
                          'bg-gray-100 text-gray-400'
                        }`}>
                          {currentStep > step.id ? <Check className="w-4 h-4" /> : step.id}
                        </div>
                        <span className="text-sm font-medium hidden sm:inline">{step.title}</span>
                      </button>
                      {index < STEPS.length - 1 && (
                        <div className={`w-8 sm:w-16 h-0.5 mx-2 ${currentStep > step.id ? 'bg-amber-500' : 'bg-gray-200'}`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Step Content */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
                <div className="mb-4">
                  <h2 className="text-xl font-bold text-gray-900">{STEPS[currentStep - 1].title}</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {currentStep === 1 && "Enter your personal information"}
                    {currentStep === 2 && "Add an optional profile photo"}
                    {currentStep === 3 && "Add your contact details"}
                    {currentStep === 4 && "Choose your signature template"}
                  </p>
                </div>
                {renderStepContent()}
              </div>

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between gap-4">
                {currentStep > 1 ? (
                  <Button variant="outline" onClick={prevStep} className="flex-1 h-12" data-testid="back-button">
                    <ChevronLeft className="w-5 h-5 mr-1" /> Back
                  </Button>
                ) : (
                  <div className="flex-1" />
                )}
                <Button
                  onClick={nextStep}
                  className="flex-1 h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                  data-testid="next-button"
                >
                  {currentStep === 4 ? <>Preview Signature</> : <>Next <ChevronRight className="w-5 h-5 ml-1" /></>}
                </Button>
              </div>

              {/* Navigation removed - preview is desktop-only */}
            </div>

            {/* Right Column: Live Preview (Desktop Only) */}
            <div className="hidden lg:block">
              <div className="sticky top-24">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-amber-500" />
                  Live Preview
                </h3>

                {/* Preview Container */}
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  {/* Template Selection */}
                  <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-4">
                    {TEMPLATES.map(template => (
                      <button
                        key={template.id}
                        onClick={() => setSelectedTemplate(template.id)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                          selectedTemplate === template.id
                            ? 'bg-amber-500 text-white shadow-md'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {template.name}
                      </button>
                    ))}
                  </div>

                  {/* Email Signature Preview */}
                  <div className="bg-gray-50 rounded-xl p-4 mb-4 overflow-x-auto">
                    <div 
                      className="bg-white border border-gray-200 rounded-lg p-3 inline-block min-w-fit"
                      dangerouslySetInnerHTML={{ __html: generateSignatureHTML() }}
                    />
                  </div>

                  {/* Info Banner */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">Get your personalized QR code</p>
                        <p className="text-xs text-amber-600 mt-0.5">
                          Sign in to get a QR code that links to your digital business card!
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Download Button */}
                  <Button
                    onClick={handleCreateAndCopy}
                    disabled={isCreatingCard}
                    className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                    data-testid="copy-html-button-desktop"
                  >
                    {isCreatingCard ? (
                      <>
                        <svg className="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Creating...
                      </>
                    ) : user ? (
                      <><Copy className="w-5 h-5 mr-2" /> Get My Signature</>
                    ) : (
                      <><Lock className="w-5 h-5 mr-2" /> Sign In to Copy HTML</>
                    )}
                  </Button>

                  {/* Quick Instructions */}
                  <div className="flex items-center justify-center gap-4 text-xs text-gray-500 mt-4">
                    <span><strong>Gmail:</strong> Settings → Signature</span>
                    <span className="text-gray-300">|</span>
                    <span><strong>Outlook:</strong> Settings → Mail → Signature</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal (Mobile Only) */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="w-[95vw] max-w-xl max-h-[90vh] overflow-y-auto p-0">
          <div className="flex justify-center pt-2 pb-1">
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>
          
          <DialogHeader className="px-4 pb-3 border-b">
            <DialogTitle className="text-lg font-semibold text-center flex items-center justify-center gap-2">
              <Mail className="w-5 h-5 text-amber-500" />
              Your Email Signature
            </DialogTitle>
          </DialogHeader>
          
          <div className="p-4">
            {/* Template Selection */}
            <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-4">
              {TEMPLATES.map(template => (
                <button
                  key={template.id}
                  onClick={() => setSelectedTemplate(template.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                    selectedTemplate === template.id
                      ? 'bg-amber-500 text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {template.name}
                </button>
              ))}
            </div>

            {/* Email Signature Preview */}
            <div className="bg-gray-50 rounded-xl p-4 mb-4 overflow-x-auto">
              <div 
                className="bg-white border border-gray-200 rounded-lg p-3 inline-block"
                dangerouslySetInnerHTML={{ __html: generateSignatureHTML() }}
              />
            </div>

            {/* Info Banner */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Sign in to get your personalized QR code that links to your digital business card!
              </p>
            </div>

            {/* Action Button */}
            <Button
              onClick={handleCreateAndCopy}
              disabled={isCreatingCard}
              className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-lg"
              data-testid="copy-html-button"
            >
              {isCreatingCard ? (
                <>
                  <svg className="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Creating...
                </>
              ) : user ? (
                <><Copy className="w-5 h-5 mr-2" /> Get My Signature</>
              ) : (
                <><Lock className="w-5 h-5 mr-2" /> Sign In to Copy HTML</>
              )}
            </Button>

            {/* Quick Instructions */}
            <div className="flex items-center justify-center gap-4 text-xs text-gray-500 mt-4">
              <span><strong>Gmail:</strong> Settings → Signature</span>
              <span className="text-gray-300">|</span>
              <span><strong>Outlook:</strong> Settings → Mail → Signature</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sign In Modal */}
      <SignInModal 
        open={showSignInModal} 
        onOpenChange={setShowSignInModal}
        noRedirect={true}
      />

      {/* Image Cropper Modal */}
      <ImageCropper
        open={showCropper}
        onClose={() => {
          setShowCropper(false);
          setImageToCrop(null);
        }}
        imageSrc={imageToCrop}
        onCropComplete={handleCropComplete}
        aspectRatio={1}
        cropShape="rect"
        title="Crop Your Photo"
      />
    </Layout>
  );
};

export default EmailSignatureGenerator;
